import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { pick } from 'lodash-es'
import { useParams } from 'react-router'
import { DBProps, IContent, OmitDBProps, dbProps } from 'types'
import { getDocs, usePrefCurrentWorkspace } from 'utils'

export const WorkspacePath = '/:workspaceId'

export type ContentRecord = Pick<IContent, keyof DBProps | (typeof partialContentFields)[number]>

const partialContentFields = [
  'parentId',
  'title',
  'value',
  'workspaceId',
  'childCount',
] as const satisfies string[]

const makeQueryKey = (workspaceIds?: string[]) => ['content', workspaceIds]

export const makeEmptyContent = (workspaceId: string, parentId = ''): OmitDBProps<IContent> => ({
  parentId,
  workspaceId,
  value: {
    en: '',
  },
  title: '',
})

export const makeEmptyChildContent = (
  workspaceId: string,
  parentId = ''
): OmitDBProps<IContent> => ({
  parentId,
  workspaceId,
  title: '',
  value: {
    en: '',
  },
})

export function useTextContent(workspaceId?: string) {
  const workspaceIdParam = useParams().workspaceId
  const [prefWorkspaceId] = usePrefCurrentWorkspace()
  const _workspaceId = workspaceId || workspaceIdParam || prefWorkspaceId
  const workspaceIds = _workspaceId ? [_workspaceId] : []

  const { data, ...rest } = useQuery({
    queryKey: makeQueryKey(workspaceIds),
    queryFn: () => getContent(workspaceIds),
    staleTime: 1000 * 30,
    enabled: !!workspaceIds?.length,
  })

  return { content: data, ...rest }
}

export function useMutateContentRecords() {
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const queryClient = useQueryClient()

  async function updateContentRecord(body: IContent) {
    if (!workspace) return

    const record = await makeContentRecord(body)

    queryClient.setQueriesData<ContentRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      content => {
        if (!content) return [record]

        const contentIndex = content.findIndex(c => c.id === record.id)
        if (contentIndex < 0) return sortContentRecords([...content, record])

        return sortContentRecords(content.map((c, i) => (i === contentIndex ? record : c)))
      }
    )
  }

  async function removeContentRecord(id: string) {
    if (!user || !workspace) return

    queryClient.setQueriesData<ContentRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      content => {
        if (!content) return []

        const contentIndex = content.findIndex(c => c.id === id)
        if (contentIndex < 0) return content

        return content.filter((_, i) => i !== contentIndex)
      }
    )
  }

  return { updateContentRecord, removeContentRecord }
}

export async function getContent(workspaceIds: string[], parentId = '') {
  const content = await getDocs({
    collection: 'content',
    fields: partialContentFields,
    constraints: [
      { field: 'workspaceId', op: 'in', value: workspaceIds },
      { field: 'parentId', op: '==', value: parentId },
    ],
  })

  return sortContentRecords(content.data)
}

function sortContentRecords(records: ContentRecord[]) {
  return records.sort((a, b) => a.title.localeCompare(b.title))
}

async function makeContentRecord(content: ContentRecord) {
  const partial = pick(content, [...dbProps, ...partialContentFields])

  return { ...partial } as ContentRecord
}
